/**
 * Simple Microsoft OAuth implementation
 * Just opens a popup and returns the authorization code
 */

import { logger } from '@sentry/react';

const MICROSOFT_AUTH_URL = 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize';
const MICROSOFT_CLIENT_ID = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
const MICROSOFT_REDIRECT_URI = `${window.location.origin}/login/microsoft`;
const MICROSOFT_SCOPES = ['openid', 'profile', 'email', 'https://graph.microsoft.com/User.Read'];
const MICROSOFT_OAUTH_STATE_STORAGE_KEY = 'microsoft-oauth-state';
const SHOULD_USE_MSW_AUTH = import.meta.env.VITE_ENABLE_MSW === 'true';

/**
 * Open popup window for Microsoft authentication and return authorization code
 */
export function signInWithMicrosoft({ timeoutMs }: { timeoutMs: number }): Promise<string> {
  if (SHOULD_USE_MSW_AUTH) {
    return Promise.resolve('mock-microsoft-oauth-token');
  }

  const state = crypto.randomUUID();
  sessionStorage.setItem(MICROSOFT_OAUTH_STATE_STORAGE_KEY, state);

  // Build authorization URL
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: MICROSOFT_CLIENT_ID,
    redirect_uri: MICROSOFT_REDIRECT_URI,
    scope: MICROSOFT_SCOPES.join(' '),
    state,
  });

  const authUrl = `${MICROSOFT_AUTH_URL}?${params.toString()}`;

  // Open popup
  const width = 600;
  const height = 600;
  const left = window.screenX + (window.innerWidth - width) / 2;
  const top = window.screenY + (window.innerHeight - height) / 2;

  const authWindow = window.open(
    authUrl,
    'MicrosoftLoginPopup',
    `width=${width},height=${height},left=${left},top=${top}`,
  );

  if (!authWindow) {
    throw new Error('Failed to open popup window. Please check your popup blocker settings.');
  }

  // Wait for redirect callback
  return new Promise((resolve, reject) => {
    let isSettled = false;

    const cleanup = ({
      closeWindow = false,
      clearTimer = true,
      clearState = false,
    }: {
      closeWindow?: boolean;
      clearTimer?: boolean;
      clearState?: boolean;
    } = {}) => {
      isSettled = true;
      clearInterval(checkInterval);

      if (clearTimer) {
        clearTimeout(timeoutTimer);
      }

      if (closeWindow && !authWindow.closed) {
        authWindow.close();
      }

      if (clearState) {
        sessionStorage.removeItem(MICROSOFT_OAUTH_STATE_STORAGE_KEY);
      }
    };

    const timeoutTimer = setTimeout(() => {
      const message = 'Microsoft sign-in is taking too long. Please retry';

      cleanup({ closeWindow: true, clearTimer: false, clearState: true });
      alert(message);
      logger.error(message, {
        extra: {
          vendor: 'microsoft',
        },
      });
      reject(new Error('Signin timeout exceeded'));
    }, timeoutMs);

    const checkInterval = setInterval(() => {
      try {
        if (isSettled) {
          return;
        }

        // Check if popup is closed
        if (authWindow.closed) {
          cleanup({ clearTimer: true, clearState: true });
          reject(new Error('Authentication cancelled by user'));
          return;
        }

        // Try to access popup location
        const popupUrl = authWindow.location.href;

        // Check if we're on the redirect URI
        if (popupUrl?.startsWith(MICROSOFT_REDIRECT_URI)) {
          cleanup({ clearTimer: true });

          // Parse the response
          const url = new URL(popupUrl);
          const code = url.searchParams.get('code');
          const error = url.searchParams.get('error');
          const callbackState = url.searchParams.get('state');
          const storedState = sessionStorage.getItem(MICROSOFT_OAUTH_STATE_STORAGE_KEY);

          // Close the popup
          if (!authWindow.closed) {
            authWindow.close();
          }

          sessionStorage.removeItem(MICROSOFT_OAUTH_STATE_STORAGE_KEY);

          if (error) {
            reject(new Error(url.searchParams.get('error_description') || error));
            return;
          }

          if (!callbackState || !storedState || callbackState !== storedState) {
            reject(new Error('Invalid OAuth state'));
            return;
          }

          if (!code) {
            reject(new Error('No authorization code received'));
            return;
          }

          // Return the authorization code
          resolve(code);
        }
      } catch {
        // Ignore cross-origin errors
      }
    }, 500);
  });
}
